import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GameScene } from '../scenes/GameScene';
import { Debug, LogCategory, LogLevel } from '../../../shared/Debug';

/**
 * Manages camera controls and different camera modes for the game
 */
export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private orbitControls: OrbitControls;
  private gameScene: GameScene;
  private domElement: HTMLElement;
  
  // Different camera positions
  private defaultCameraPosition: THREE.Vector3;
  private defaultTarget: THREE.Vector3;
  private topCameraPosition: THREE.Vector3;
  private topTarget: THREE.Vector3;
  private firstPersonMode: boolean = false;
  private followingPlayer: boolean = false;
  private fixedFollowMode: boolean = false;
  private currentMode: string = 'fixed-follow';
  private orbitEnabled: boolean = false; // Default orbit controls to disabled
  private isPortrait: boolean = false;
  private landscapeCameraPosition: THREE.Vector3 = new THREE.Vector3(0, 25, 40);
  private portraitCameraPosition: THREE.Vector3 = new THREE.Vector3(0, 25, 80);
  private landscapeLookTargetHeight: number = 3.0; // Height above player to look at in fixed-follow mode
  private portraitLookTargetHeight: number = 5.0; // Height above player to look at in fixed-follow mode
  private lookTargetHeight: number = this.landscapeLookTargetHeight; // Height above player to look at in fixed-follow mode

  // Camera follow settings
  private followDamping: number = 0.09; // Lower = smoother but more lag
  private currentLookTarget: THREE.Vector3 = new THREE.Vector3();
  private currentCameraPosition: THREE.Vector3 = new THREE.Vector3();
  private deadZone: number = 2.0; // Distance in units within which player movement is ignored
  
  
  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement, gameScene: GameScene) {
    this.camera = camera;
    this.domElement = domElement;
    this.gameScene = gameScene;
    
   
    
    // Initialize the orbit controls
    this.orbitControls = new OrbitControls(this.camera, this.domElement);
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.1;
    this.orbitControls.enableZoom = true;
    this.orbitControls.enablePan = true;
    this.orbitControls.maxPolarAngle = Math.PI * 0.45; // Limit vertical angle to avoid going below ground
    this.orbitControls.minDistance = 5; // Minimum zoom distance
    this.orbitControls.maxDistance = 100; // Maximum zoom distance
    this.orbitControls.enabled = this.orbitEnabled; // Set orbit controls to disabled by default
    
    // Add angle limits for fixed-follow mode
    this.orbitControls.minAzimuthAngle = -Math.PI * 0.25; // Limit horizontal rotation to 45 degrees left
    this.orbitControls.maxAzimuthAngle = Math.PI * 0.25;  // Limit horizontal rotation to 45 degrees right
    this.orbitControls.minPolarAngle = Math.PI * 0.2;     // Limit vertical angle to avoid looking too high
    this.orbitControls.maxPolarAngle = Math.PI * 0.4;     // Limit vertical angle to avoid looking too low
    
    // Save default camera positions
    this.defaultCameraPosition = new THREE.Vector3(0, 25, this.landscapeCameraPosition.z);
    this.defaultTarget = new THREE.Vector3(0, 1, 0);
    
    // Top-down view position
    this.topCameraPosition = new THREE.Vector3(0, 70, 0);
    this.topTarget = new THREE.Vector3(0, 0, 0);
    
    // Set initial position
    this.setCameraMode('fixed-follow');
    
    // Add keyboard event listeners for camera control
    this.setupKeyboardControls();

     // Initialize orientation detection    
     this.updateOrientation(true);
     window.addEventListener('resize', () => this.updateOrientation());
  }
  
  /**
   * Setup keyboard controls for camera
   */
  private setupKeyboardControls(): void {
    window.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'c':
        case 'C':
          // Cycle through camera modes
          this.cycleCameraMode();
          break;
        case 'f':
        case 'F':
          // Toggle first-person mode
          this.toggleFirstPersonMode();
          break;
        case 'r':
        case 'R':
          // Reset camera to default position
          this.resetCamera();
          break;
        case 'p':
        case 'P':
          // Toggle player following mode
          this.togglePlayerFollowing();
          break;
        case 'o':
        case 'O':
          // Toggle orbit controls
          this.toggleOrbitControls();
          break;
        case 'm':
        case 'M':
          // Toggle fixed-follow mode
          this.toggleFixedFollowMode();
          break;
      }
    });
  }
  
  /**
   * Update the camera controls each frame
   */
  public update(deltaTime: number): void {
    // Update orbit controls only if enabled
    if (this.orbitControls.enabled) {
      this.orbitControls.update();
    }
    
    // In first-person, following, or fixed-follow mode, update camera position to follow player
    if ((this.firstPersonMode || this.followingPlayer || this.fixedFollowMode) && this.gameScene) {
      const playerPosition = this.gameScene.getPlayerPosition();
      if (playerPosition) {
        if (this.firstPersonMode) {
          // First person mode - position camera at player's head
          const headHeight = 1.7; // Approximate player head height
          this.camera.position.set(
            playerPosition.x,
            playerPosition.y + headHeight,
            playerPosition.z
          );
          
          // Get player's rotation and calculate look direction
          const playerRotation = this.gameScene.getPlayerRotation();
          if (playerRotation) {
            const lookDistance = 5;
            const lookTarget = new THREE.Vector3(
              playerPosition.x + Math.sin(playerRotation.y) * lookDistance,
              playerPosition.y + headHeight/2,
              playerPosition.z + Math.cos(playerRotation.y) * lookDistance
            );
            this.camera.lookAt(lookTarget);
          } else {
            // Fallback to looking forward if rotation is not available
            this.camera.lookAt(
              playerPosition.x,
              playerPosition.y + headHeight,
              playerPosition.z - 5
            );
          }
          
          // Disable orbit controls in first person mode
          this.orbitControls.enabled = false;
          this.orbitEnabled = false;
        } else if (this.followingPlayer) {
          // Follow player mode - position camera behind and above player
          const offsetY = 30;
          const offsetZ = 45;
          const targetPosition = new THREE.Vector3(
            playerPosition.x,
            playerPosition.y + offsetY,
            playerPosition.z + offsetZ
          );
          
          // Only lerp if orbit controls are disabled
          if (!this.orbitControls.enabled) {
            // Calculate frame-rate independent damping factor
            const dampingFactor = Math.min(1.0, this.followDamping * (deltaTime * 60)); // Normalized to 60 FPS
            
            // Smoothly interpolate camera position
            this.camera.position.lerp(targetPosition, dampingFactor);
            
            // Smoothly interpolate look target
            this.currentLookTarget.lerp(playerPosition, dampingFactor);
            this.camera.lookAt(this.currentLookTarget);
          } else {
            // When orbit controls are enabled, only update the target position
            this.currentLookTarget.copy(playerPosition);
            this.orbitControls.target.copy(this.currentLookTarget);
          }
          
          // Update orbit controls target
          this.orbitControls.target.copy(this.currentLookTarget);
          this.orbitControls.enabled = this.orbitEnabled;
        } else if (this.fixedFollowMode) {
          // Fixed-follow mode - maintain camera position but look at player
          // Calculate distance to current look target
          const distanceToTarget = playerPosition.distanceTo(this.currentLookTarget);
          
          // Only update look target if player has moved outside dead zone
          if (distanceToTarget > this.deadZone) {
            // Create target position
            const targetPosition = new THREE.Vector3(
              playerPosition.x,
              playerPosition.y + this.lookTargetHeight,
              playerPosition.z
            );
            
            // Only lerp if orbit controls are disabled
            if (!this.orbitControls.enabled) {
              // Calculate frame-rate independent damping factor
              const dampingFactor = Math.min(1.0, this.followDamping * (deltaTime * 60)); // Normalized to 60 FPS
              
              // Smoothly interpolate look target
              this.currentLookTarget.lerp(targetPosition, dampingFactor);
            } else {
              // When orbit controls are enabled, directly update the target
              this.currentLookTarget.copy(targetPosition);
            }
          }
          // Check if the camera angle is within the min/max angular limits of the orbit control
          // Calculate the current camera angle relative to the target
          const cameraDirection = new THREE.Vector3().subVectors(
            this.camera.position,
            this.currentLookTarget
          ).normalize();
          
          // Calculate the horizontal angle (azimuth)
          const azimuthAngle = Math.atan2(cameraDirection.x, cameraDirection.z);
          
          // Calculate the vertical angle (polar)
          const polarAngle = Math.acos(cameraDirection.y);
          
          // Check if angles are within the orbit control limits
          const withinAzimuthLimits = 
            azimuthAngle >= this.orbitControls.minAzimuthAngle && 
            azimuthAngle <= this.orbitControls.maxAzimuthAngle;
            
          const withinPolarLimits = 
            polarAngle >= this.orbitControls.minPolarAngle && 
            polarAngle <= this.orbitControls.maxPolarAngle;
            
          // Only update lookAt if within limits and orbit controls are disabled
          if ((withinAzimuthLimits || withinPolarLimits) && !this.orbitControls.enabled) {
            // The camera angle is within limits, so we can update the lookAt
            this.camera.lookAt(this.currentLookTarget);
          }
          
          // Update orbit controls target
          this.orbitControls.target.copy(this.currentLookTarget);
          this.orbitControls.enabled = this.orbitEnabled;
        }
      }
    }
  }
  
  /**
   * Cycle through different camera modes
   */
  public cycleCameraMode(): void {
    const modes = ['fixed-follow', 'default', 'top', 'orbit'];
    const currentIndex = modes.indexOf(this.currentMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    this.setCameraMode(modes[nextIndex]);
  }
  
  /**
   * Set the camera to a specific mode
   * @param mode The camera mode to set
   */
  public setCameraMode(mode: string): void {
    this.currentMode = mode;
    this.firstPersonMode = false;
    this.followingPlayer = false;
    this.fixedFollowMode = false;
    
    // Reset interpolation targets when changing modes
    this.currentLookTarget.set(0, 1, 0);
    
    switch (mode) {
      case 'default':
        // Default side view
        this.camera.position.copy(this.defaultCameraPosition);
        this.camera.lookAt(this.defaultTarget);
        this.orbitControls.target.copy(this.defaultTarget);
        this.orbitControls.enabled = this.orbitEnabled;
        // Remove angle limits for default mode
        this.orbitControls.minAzimuthAngle = -Infinity;
        this.orbitControls.maxAzimuthAngle = Infinity;
        this.orbitControls.minPolarAngle = 0;
        this.orbitControls.maxPolarAngle = Math.PI;
        break;
      case 'top':
        // Top-down view
        this.camera.position.copy(this.topCameraPosition);
        this.camera.lookAt(this.topTarget);
        this.orbitControls.target.copy(this.topTarget);
        this.orbitControls.enabled = this.orbitEnabled;
        // Remove angle limits for top view
        this.orbitControls.minAzimuthAngle = -Infinity;
        this.orbitControls.maxAzimuthAngle = Infinity;
        this.orbitControls.minPolarAngle = 0;
        this.orbitControls.maxPolarAngle = Math.PI;
        break;
      case 'orbit':
        // Free orbit mode - maintain current position but enable controls
        this.orbitEnabled = true;
        this.orbitControls.enabled = true;
        // Remove angle limits for orbit mode
        this.orbitControls.minAzimuthAngle = -Infinity;
        this.orbitControls.maxAzimuthAngle = Infinity;
        this.orbitControls.minPolarAngle = 0;
        this.orbitControls.maxPolarAngle = Math.PI;
        break;
      case 'fixed-follow':
        // Fixed-follow mode - maintain camera position but follow player
        this.fixedFollowMode = true;
        this.camera.position.copy(this.defaultCameraPosition);
        this.orbitControls.enabled = this.orbitEnabled;
        // Apply strict angle limits for fixed-follow mode
        this.orbitControls.minAzimuthAngle = -Math.PI * 0.25; // 45 degrees left
        this.orbitControls.maxAzimuthAngle = Math.PI * 0.25;  // 45 degrees right
        this.orbitControls.minPolarAngle = Math.PI * 0.2;     // 36 degrees from vertical
        this.orbitControls.maxPolarAngle = Math.PI * 0.4;     // 72 degrees from vertical

        this.camera.lookAt(this.defaultTarget);
        break;
    }

    Debug.info(`Camera mode set to ${mode}`, LogCategory.GENERAL);
    
    // Update camera
    this.camera.updateProjectionMatrix();
  }
  
  /**
   * Toggle first-person mode
   */
  public toggleFirstPersonMode(): void {
    this.firstPersonMode = !this.firstPersonMode;
    if (this.firstPersonMode) {
      this.followingPlayer = false;
      this.currentMode = 'firstPerson';
      this.orbitControls.enabled = false;
      this.orbitEnabled = false;
    } else {
      this.setCameraMode('fixed-follow');
    }
  }
  
  /**
   * Toggle player following mode
   */
  public togglePlayerFollowing(): void {
    this.followingPlayer = !this.followingPlayer;
    if (this.followingPlayer) {
      this.firstPersonMode = false;
      this.currentMode = 'following';
      this.orbitControls.enabled = this.orbitEnabled;
    } else {
      this.setCameraMode('fixed-follow');
    }
  }
  
  /**
   * Toggle orbit controls on/off
   */
  public toggleOrbitControls(): void {
    this.orbitEnabled = !this.orbitEnabled;
    
    // Only enable orbit controls if we're not in first person mode
    if (!this.firstPersonMode) {
      this.orbitControls.enabled = this.orbitEnabled;
    }
    
    Debug.info(`Orbit controls ${this.orbitEnabled ? 'enabled' : 'disabled'}`, LogCategory.GENERAL);
  }
  
  /**
   * Reset camera to default position
   */
  public resetCamera(): void {
    this.setCameraMode('fixed-follow');
  }
  
  /**
   * Toggle fixed-follow mode
   */
  public toggleFixedFollowMode(): void {
    this.fixedFollowMode = !this.fixedFollowMode;
    if (this.fixedFollowMode) {
      this.firstPersonMode = false;
      this.followingPlayer = false;
      this.currentMode = 'fixed-follow';
      this.orbitControls.enabled = this.orbitEnabled;
    } else {
      this.setCameraMode('fixed-follow');
    }
  }
  
  /**
   * Dispose of controls when no longer needed
   */
  public dispose(): void {
    this.orbitControls.dispose();
    window.removeEventListener('keydown', () => {});
  }
  
  /**
   * Update orientation state and camera position
   */
  private updateOrientation(forceUpdate: boolean = false): void {
    const wasPortrait = this.isPortrait;
    this.isPortrait = window.innerHeight > window.innerWidth;
    
    // Update camera Z position if orientation changed
    if (forceUpdate || wasPortrait !== this.isPortrait) {
      this.defaultCameraPosition = this.isPortrait ? this.portraitCameraPosition : this.landscapeCameraPosition;
      this.lookTargetHeight = this.isPortrait ? this.portraitLookTargetHeight : this.landscapeLookTargetHeight;
      Debug.info(`Camera position set to ${this.isPortrait ? 'portrait' : 'landscape'} ${JSON.stringify(this.defaultCameraPosition)}`, LogCategory.GENERAL);
      
      this.camera.position.copy(this.defaultCameraPosition);
    }
  }
} 